import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ServiceCategory, SubService, SubServiceMedia } from '@/lib/types';
import { Database } from '@/integrations/supabase/types';
import { PostgrestError } from '@supabase/supabase-js';

type ServiceRow = Database['public']['Tables']['services']['Row'];
type SubServiceRow = Database['public']['Tables']['sub_services']['Row'];
type SubServiceMediaRow = Database['public']['Tables']['sub_service_media']['Row'];

/** 
 * Phase 2 & 3 Strategy: 
 * We split data fetching to handle 1M+ users efficiently.
 * - useServices: Fetches ONLY category summaries for Home/Search. (Lightweight)
 * - useInfiniteServices: Support Infinite Scroll (Phase 3).
 * - useServiceDetail: Fetches full details with sub-services/media ON-DEMAND when needed. (On-Demand)
 */

const PAGE_SIZE = 20;

const fetchCategories = async ({ pageParam = 0, includeInactive = false }): Promise<ServiceCategory[]> => {
  let query = supabase.from('services').select('*, sub_services(count), service_cities(city_id)');
  
  if (!includeInactive) {
    query = query.eq('status', 'active');
  }

  const sRes = await query
    .order('sort_order', { ascending: true })
    .range(pageParam * PAGE_SIZE, (pageParam + 1) * PAGE_SIZE - 1);

  if (sRes.error) throw sRes.error;
  
  const sData = (sRes.data || []) as (ServiceRow & { sub_services: { count: number }[]; service_cities: { city_id: string }[] })[];

  return sData.map(s => ({
    id: s.id,
    name: s.name,
    icon: s.icon,
    description: s.description,
    startingPrice: s.starting_price,
    rating: Number(s.rating),
    reviewCount: s.review_count,
    imageUrl: s.image_url || undefined,
    priceType: (s.price_type as "range" | "fixed") || 'fixed',
    priceMin: s.price_min || 0,
    priceMax: s.price_max || 0,
    advancedListing: s.advanced_listing || false,
    isRare: s.is_rare || false,
    status: (s.status as 'active' | 'inactive') || 'active',
    slug: s.slug || s.name.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-').replaceAll(/(^-|-$)+/g, ''),
    cityIds: (s.service_cities || []).map(sc => sc.city_id),
    subServices: [],
    subServiceCount: s.sub_services?.[0]?.count || 0,
  }));
};

const toSlug = (text: string) => text.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-').replaceAll(/(^-|-$)+/g, '');

export const fetchServiceDetail = async (identifier: string): Promise<ServiceCategory | null> => {
  const cleanId = identifier.trim().toLowerCase();
  const slugifiedId = toSlug(identifier);
  
  // Try UUID first if it looks like one, otherwise fallback to Slug
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);
  
  let sData: ServiceRow | null = null;
  let sErr: PostgrestError | null = null;

  if (isUuid) {
    const { data: uuidData, error: uuidErr } = await supabase
      .from('services')
      .select('*')
      .or(`id.eq.${identifier},slug.eq.${slugifiedId}`)
      .maybeSingle();
    
    sData = uuidData;
    sErr = uuidErr;
  } 
  
  // If not found by UUID or not a UUID, try by Slug or slugified Name
  if (!sData) {
    const { data: slugData, error: slugErr } = await supabase
      .from('services')
      .select('*')
      .or(`slug.ilike.${slugifiedId},slug.ilike.${cleanId}`)
      .maybeSingle();
    
    sData = slugData;
    sErr = slugErr;
  }

  // Final fallback: Exhaustive Search (Limited to top 100 to avoid performance hit)
  if (!sData) {
    const { data: allData } = await supabase.from('services').select('*').limit(100);
    sData = (allData?.find((s: ServiceRow) => 
      (s.slug && (s.slug.toLowerCase() === slugifiedId || s.slug.toLowerCase() === cleanId)) || 
      (s.name && toSlug(s.name) === slugifiedId) ||
      (s.id === identifier)
    ) || null);
  }

  if (sErr) {
    console.error("fetchServiceDetail error:", sErr);
    return null;
  }
  
  if (!sData) return null;
  const s = sData;

  // 2. Fetch children (optimized)
  const [subRes, scRes] = await Promise.all([
    supabase.from('sub_services').select('*').eq('service_id', s.id).order('sort_order', { ascending: true }),
    supabase.from('service_cities').select('city_id').eq('service_id', s.id),
  ]);

  const subData = (subRes.data || []) as SubServiceRow[];
  const scData = (scRes.data || []) as { city_id: string }[];
  
  // Precision Media Fetching
  const subIds = subData.map(sub => sub.id);
  const { data: mediaDataRaw = [] } = subIds.length > 0 
    ? await supabase.from('sub_service_media').select('*').in('sub_service_id', subIds).order('sort_order', { ascending: true })
    : { data: [] };

  const mediaData = (mediaDataRaw || []) as SubServiceMediaRow[];

  return {
    id: s.id,
    name: s.name,
    icon: s.icon,
    description: s.description,
    startingPrice: s.starting_price,
    rating: Number(s.rating),
    reviewCount: s.review_count,
    imageUrl: s.image_url || undefined,
    priceType: (s.price_type as "range" | "fixed") || 'fixed',
    priceMin: s.price_min || 0,
    priceMax: s.price_max || 0,
    advancedListing: s.advanced_listing || false,
    isRare: s.is_rare || false,
    status: (s.status as 'active' | 'inactive') || 'active',
    slug: s.slug || toSlug(s.name),
    cityIds: scData.map(sc => sc.city_id),
    subServices: subData.map((sub): SubService => ({
      id: sub.id,
      name: sub.name,
      price: sub.price,
      duration: sub.duration,
      description: sub.description,
      detailedDescription: sub.detailed_description || '',
      mrpMarkupPercentage: sub.mrp_markup_percentage || undefined,
      mrpOverride: sub.mrp_override || undefined,
      media: (mediaData || [])
        .filter(m => m.sub_service_id === sub.id)
        .map((m): SubServiceMedia => ({
          id: m.id,
          sub_service_id: m.sub_service_id,
          type: m.type as 'image' | 'video',
          url: m.url,
          sort_order: m.sort_order,
        })),
    })),
  };
};

// Hook for Home / Search (Lightweight)
export const useServices = (includeInactive = false) => {
  const { data: services = [], isLoading, error, refetch } = useQuery({
    queryKey: ['services-summary', includeInactive],
    queryFn: () => fetchCategories({ pageParam: 0, includeInactive }),
    staleTime: 60 * 60 * 1000, 
    refetchOnWindowFocus: false,
  });

  return { services, loading: isLoading, error: error ? (error as Error).message : null, refetch };
};

// Hook for Infinite Scroll (Scaling)
export const useInfiniteServices = (includeInactive = false) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch
  } = useInfiniteQuery({
    queryKey: ['services-infinite', includeInactive],
    queryFn: (ctx) => fetchCategories({ ...ctx, includeInactive }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.length === PAGE_SIZE ? allPages.length : undefined;
    },
    staleTime: 5 * 60 * 1000,
  });

  const services = useMemo(() => data?.pages.flat() || [], [data?.pages]);

  return { 
    services, 
    loading: isLoading, 
    isFetchingNextPage, 
    hasNextPage, 
    fetchNextPage, 
    error: error ? (error as Error).message : null, 
    refetch 
  };
};

// Hook for Global Deep Search (Scaling)
export const useGlobalSearch = (query: string) => {
  return useQuery({
    queryKey: ['global-search', query],
    queryFn: async () => {
      if (!query.trim()) return [];
      const { data, error } = await supabase.rpc('global_search', { query_text: query.trim() });
      if (error) throw error;
      return (data || []) as Record<string, unknown>[];
    },
    enabled: query.trim().length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook for Detail Pages (Deep Fetch on Demand)
export const useServiceDetail = (identifier: string | undefined) => {
  const { data: service = null, isLoading, error, refetch } = useQuery({
    queryKey: ['service-detail', identifier],
    queryFn: () => identifier ? fetchServiceDetail(identifier) : Promise.resolve(null),
    staleTime: 15 * 60 * 1000, // 15 Minutes (Million-User UX: Instant open for 15 mins)
    gcTime: 24 * 60 * 60 * 1000, // Keep in memory for 24 hours (Garbage Collection time)
    enabled: !!identifier,
  });

  return { service, loading: isLoading, error: error ? (error as Error).message : null, refetch };
};
